import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../../common/Button';
import { Loader } from '../../App/components/Loader';
import { getCurrentClassroomId } from '../selectors/users';
import { getClassroom, regenerateJoinCode } from '../../../firebase/classrooms';
import { showToast } from '../actions/toast';

/**
 * Manage the current classroom's join code (staff only): show the code and regenerate it (which
 * invalidates the previous one). Scoped to the user's current classroom.
 */
const ManageJoinCodeModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentClassroomId = useSelector(getCurrentClassroomId);

  const [classroom, setClassroom] = useState(null); // null = loading, false = none
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!currentClassroomId) {
      setClassroom(false);
      return () => {};
    }
    getClassroom(currentClassroomId)
      .then((room) => {
        if (!cancelled) setClassroom(room || false);
      })
      .catch(() => {
        if (!cancelled) setClassroom(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentClassroomId]);

  async function handleRegenerate() {
    if (!classroom) return;
    setRegenerating(true);
    try {
      const { joincode, expiresAt } = await regenerateJoinCode(classroom.id);
      setClassroom((room) => ({
        ...room,
        joincode,
        joincodeExpiresAt: expiresAt
      }));
      dispatch(showToast('Toast.JoinCodeRegenerated', 3500));
    } catch (e) {
      dispatch(showToast('Toast.JoinCodeRegenerateError', 4000));
    } finally {
      setRegenerating(false);
    }
  }

  if (classroom === null) {
    return (
      <div className="classroom-modal" style={{ minHeight: '6rem' }}>
        <Loader />
      </div>
    );
  }

  if (!classroom) {
    return (
      <div className="classroom-modal">
        <p>
          {t('ClassroomMenu.NoCurrentClassroom', {
            defaultValue: 'You are not in a classroom yet.'
          })}
        </p>
      </div>
    );
  }

  // The join code is only usable until joincodeExpiresAt; past that, joinClassroom rejects it, so
  // don't flash an unusable code — surface "expired" and push the instructor toward Regenerate.
  const isExpired =
    !classroom.joincodeExpiresAt || classroom.joincodeExpiresAt <= Date.now();
  const validUntil = classroom.joincodeExpiresAt
    ? new Date(classroom.joincodeExpiresAt).toLocaleString()
    : '';

  return (
    <div className="classroom-modal">
      <p className="classroom-modal__name">{classroom.name}</p>

      {isExpired ? (
        <p className="classroom-modal__code-expired" role="status">
          {t('ManageJoinCodeModal.Expired', {
            defaultValue:
              'This join code has expired. Generate a new one below.'
          })}
        </p>
      ) : (
        <React.Fragment>
          <label
            htmlFor="join-code-value"
            className="classroom-modal__code-label"
          >
            {t('ManageJoinCodeModal.CodeLabel', { defaultValue: 'Join code' })}
          </label>
          <div id="join-code-value" className="classroom-modal__code-big">
            {classroom.joincode}
          </div>
          <p className="classroom-modal__code-valid">
            {t('ManageJoinCodeModal.ValidUntil', {
              defaultValue: 'Valid until {{when}}',
              when: validUntil
            })}
          </p>
        </React.Fragment>
      )}

      <Button
        onClick={handleRegenerate}
        disabled={regenerating}
        className={
          isExpired ? 'classroom-modal__regenerate--attention' : undefined
        }
      >
        {regenerating
          ? t('ViewClassroomModal.Regenerating', {
              defaultValue: 'Regenerating…'
            })
          : t('ViewClassroomModal.Regenerate', {
              defaultValue: 'Regenerate join code'
            })}
      </Button>
    </div>
  );
};

export default ManageJoinCodeModal;
